import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Upload, Eye, Mail, CheckCircle, AlertCircle, Loader, FileText, Send, Calendar, X, Sun, Moon, Monitor, Smartphone } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

interface CTIData {
  date: string;
  holiday_notice?: string;
  headlines: Array<{
    title: string;
    date: string;
    content: string;
    analyst_comments: string;
  }>;
  other_news: Record<string, string[]>;
  technical_intelligence: Record<string, string[]>;
  hyperlinks: Record<string, string>;
}

const CTIBriefingManager: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedLists, setSelectedLists] = useState<string[]>(['general']);
  const [customSubject, setCustomSubject] = useState('');
  const [testEmails, setTestEmails] = useState<string>('');
  const [ctiData, setCtiData] = useState<CTIData | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [previewTheme, setPreviewTheme] = useState<'light' | 'dark'>('light');
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [nbaWarning, setNbaWarning] = useState<{show: boolean, terms: string[], contexts: string[]}>({show: false, terms: [], contexts: []});

  const listOptions = [
    { key: 'general', label: 'General Security', description: 'Main newsletter list' },
    { key: 'application_security', label: 'Application Security', description: 'App security focused' },
    { key: 'devsecops', label: 'DevSecOps', description: 'DevOps security automation' },
    { key: 'offensive_security', label: 'Offensive Security', description: 'Penetration testing & red team' },
    { key: 'cloud_security', label: 'Cloud Security', description: 'AWS/Azure/GCP security' },
    { key: 'advisory_services', label: 'Advisory Services', description: 'Security consulting' }
  ];

  const handleFileUpload = async (file: File, forceUpload = false) => {
    setIsUploading(true);
    setStatus('idle');
    
    const formData = new FormData();
    formData.append('file', file);
    if (forceUpload) {
      formData.append('force_upload', 'true');
    }

    try {
      const response = await fetch('/api/cti/upload', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        // Always set the selected file for potential force upload
        setSelectedFile(file);
        
        // Check for NBA content warning (only if not force upload)
        if (!forceUpload && data.nba_warning?.detected) {
          setNbaWarning({
            show: true,
            terms: data.nba_warning.terms || [],
            contexts: data.nba_warning.contexts || []
          });
          return;
        }

        setCtiData(data.data);
        setStatus('success');
        if (forceUpload && data.nba_warning?.detected) {
          setMessage('CTI document uploaded with NBA content override!');
        } else {
          setMessage('CTI document parsed successfully!');
        }
        
        // Auto-generate subject if date is available
        if (data.data.date && !customSubject) {
          setCustomSubject(`🛡️ CIPHER Security CTI Briefing - ${data.data.date}`);
        }
      } else {
        setStatus('error');
        setMessage(data.error || 'Failed to parse document');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };


  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.name.toLowerCase().endsWith('.docx') || file.name.toLowerCase().endsWith('.doc')) {
        handleFileUpload(file);
      } else {
        setStatus('error');
        setMessage('Please upload a Word document (.docx or .doc)');
      }
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleListToggle = (listKey: string) => {
    setSelectedLists(prev => 
      prev.includes(listKey)
        ? prev.filter(key => key !== listKey)
        : [...prev, listKey]
    );
  };

  const handlePreview = async () => {
    if (!ctiData) {
      setStatus('error');
      setMessage('Please upload a CTI document first');
      return;
    }

    setIsGeneratingPreview(true);
    setStatus('idle');
    try {
      const response = await fetch('/api/cti/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ cti_data: ctiData })
      });

      const data = await response.json();
      if (data.success) {
        setPreviewHtml(data.html_content);
        setShowPreview(true);
        // Write content to iframe after modal opens
        setTimeout(() => {
          updateIframeContent(data.html_content);
        }, 100);
      } else {
        setStatus('error');
        setMessage(data.error || 'Preview failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Failed to generate preview');
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  const updateIframeContent = (htmlContent: string) => {
    if (iframeRef.current && iframeRef.current.contentDocument) {
      const doc = iframeRef.current.contentDocument;
      
      // Apply theme styles and responsive viewport
      let themedContent = htmlContent;
      
      // Add responsive viewport meta tag and device-specific styles
      const viewportStyles = previewDevice === 'mobile' ? `
        <style>
          @media screen {
            body { 
              width: 100% !important;
              min-width: 100% !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            .email-container {
              width: 100% !important;
              max-width: 100% !important;
              margin: 0 !important;
            }
            table[class="email-container"] {
              width: 100% !important;
              max-width: 100% !important;
            }
          }
        </style>
      ` : '';
      
      if (previewTheme === 'dark') {
        themedContent = applyDarkThemeToEmail(htmlContent);
      } else {
        // Ensure clean white background for light theme
        themedContent = htmlContent.replace(
          '</head>',
          '<style>html, body { background-color: #ffffff !important; }</style></head>'
        );
      }
      
      // Add viewport styles
      themedContent = themedContent.replace('</head>', viewportStyles + '</head>');
      
      doc.open();
      doc.write(themedContent);
      doc.close();
      
      // Force iframe to use mobile viewport width
      if (previewDevice === 'mobile' && doc.documentElement) {
        doc.documentElement.style.width = '100%';
        doc.body.style.width = '100%';
      }
    }
  };

  const applyDarkThemeToEmail = (html: string) => {
    // Inject dark theme CSS overrides
    const darkThemeStyles = `
      <style>
        /* Dark theme overrides */
        html {
          background-color: #0a0a0a !important;
        }
        body { 
          background-color: #0a0a0a !important; 
          color: #e0e0e0 !important;
        }
        .email-container { 
          background-color: #0a0a0a !important;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3) !important;
        }
        .header {
          background: linear-gradient(135deg, #00a86b 0%, #06b6d4 100%) !important;
        }
        .cti-header {
          background-color: #0a0a0a !important;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
        }
        .cti-title {
          color: #ffffff !important;
        }
        .content {
          background-color: #0a0a0a !important;
          color: #e0e0e0 !important;
        }
        .headline-item {
          background: #1a1a2e !important;
          border: 1px solid rgba(255, 255, 255, 0.08) !important;
        }
        .headline-title {
          color: #06b6d4 !important;
        }
        .headline-date, .headline-content, .analyst-comment {
          color: #e0e0e0 !important;
        }
        .section-title {
          color: #ffffff !important;
          border-bottom-color: #00a86b !important;
        }
        .news-category {
          color: #06b6d4 !important;
        }
        .news-item {
          color: #e0e0e0 !important;
        }
        .footer {
          background: #000000 !important;
          border-top: 1px solid rgba(255, 255, 255, 0.08) !important;
        }
        .footer a {
          color: #06b6d4 !important;
        }
        /* Lists */
        ul li, ol li {
          color: #e0e0e0 !important;
        }
        /* Links in content */
        .content a {
          color: #06b6d4 !important;
        }
      </style>
    `;
    
    // Insert the dark theme styles before the closing </head> tag
    return html.replace('</head>', darkThemeStyles + '</head>');
  };

  // Update iframe content when device or theme changes
  useEffect(() => {
    if (showPreview && previewHtml) {
      updateIframeContent(previewHtml);
    }
  }, [previewDevice, previewTheme, showPreview]);

  const handleSendTest = async () => {
    if (!ctiData) {
      setStatus('error');
      setMessage('Please upload a CTI document first');
      return;
    }

    if (!testEmails.trim()) {
      setStatus('error');
      setMessage('Please enter test email addresses');
      return;
    }

    const emailList = testEmails.split(',').map(email => email.trim()).filter(email => email);
    
    setIsLoading(true);
    setStatus('idle');

    try {
      const response = await fetch('/api/cti/send-test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cti_data: ctiData,
          test_emails: emailList,
          subject: customSubject || undefined
        })
      });

      const data = await response.json();
      if (data.success) {
        setStatus('success');
        setMessage(`Test CTI briefing sent to ${emailList.length} recipients`);
      } else {
        setStatus('error');
        setMessage(data.error || 'Test email failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Failed to send test email');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendCampaign = async () => {
    if (!ctiData) {
      setStatus('error');
      setMessage('Please upload a CTI document first');
      return;
    }

    if (selectedLists.length === 0) {
      setStatus('error');
      setMessage('Please select at least one mailing list');
      return;
    }

    setIsLoading(true);
    setStatus('idle');

    try {
      const response = await fetch('/api/cti/send-campaign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          cti_data: ctiData,
          target_lists: selectedLists,
          subject: customSubject || undefined,
          send_immediately: true
        })
      });

      const data = await response.json();
      if (data.success) {
        setStatus('success');
        setMessage(`CTI briefing campaign sent successfully! Campaign ID: ${data.campaign_id}`);
      } else {
        setStatus('error');
        setMessage(data.error || 'Campaign failed');
      }
    } catch (error) {
      setStatus('error');
      setMessage('Failed to send campaign');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="cti-briefing-manager">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-white mb-1">
            CTI Briefing Manager
          </h1>
          <p className="text-sm text-gray-400">
            Upload daily CTI briefings and send them as professional newsletters
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload & Configuration */}
          <div className="bg-[#0a0a0a] backdrop-blur-sm rounded-xl p-6 border border-white/[0.08]">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
              <Upload className="w-4 h-4 mr-2" />
              Upload CTI Document
            </h2>

            {/* File Upload Area */}
              <div 
                className={`border-2 border-dashed rounded-lg p-8 text-center mb-6 transition-colors ${
                  isUploading ? 'border-[#06b6d4] bg-[#06b6d4]/10' : 'border-gray-600 hover:border-gray-500'
                }`}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                {isUploading ? (
                  <div className="flex flex-col items-center">
                    <Loader className="w-8 h-8 text-[#06b6d4] animate-spin mb-3" />
                    <p className="text-[#06b6d4] font-medium">Processing document...</p>
                  </div>
                ) : selectedFile ? (
                  <div className="flex flex-col items-center">
                    <FileText className="w-8 h-8 text-[#00a86b] mb-3" />
                    <p className="text-[#00a86b] font-medium">{selectedFile.name}</p>
                    <p className="text-gray-400 text-sm">Document parsed successfully</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="w-8 h-8 text-gray-400 mb-3" />
                    <p className="text-white mb-2">Drop your CTI briefing here</p>
                    <p className="text-gray-400 text-sm mb-4">or click to browse</p>
                    <input
                      type="file"
                      accept=".docx,.doc"
                      onChange={handleFileInputChange}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="px-4 py-2 bg-[#0a0a0a] hover:bg-white/10 text-white rounded-lg cursor-pointer transition-colors border border-white/[0.08]"
                    >
                      Choose File
                    </label>
                  </div>
                )}
              </div>

            {/* CTI Data Summary */}
            {ctiData && (
              <div className="bg-gray-800/50 rounded-lg p-4 mb-6 border border-gray-700/50">
                <h3 className="text-white font-medium mb-3">Parsed Content Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-300">Date:</span>
                    <span className="text-white">{ctiData.date || 'Not specified'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Headlines:</span>
                    <span className="text-white">{ctiData.headlines?.length || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">News Categories:</span>
                    <span className="text-white">{Object.keys(ctiData.other_news || {}).length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Technical Sections:</span>
                    <span className="text-white">{Object.keys(ctiData.technical_intelligence || {}).length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-300">Hyperlinks:</span>
                    <span className="text-white">{Object.keys(ctiData.hyperlinks || {}).length}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Custom Subject */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email Subject
              </label>
              <input
                type="text"
                value={customSubject}
                onChange={(e) => setCustomSubject(e.target.value)}
                placeholder="Auto-generated from CTI date"
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-white/[0.15]"
              />
            </div>

            {/* List Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Target Lists
              </label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {listOptions.map(option => (
                  <label key={option.key} className="flex items-start cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedLists.includes(option.key)}
                      onChange={() => handleListToggle(option.key)}
                      className="mt-1 mr-3 accent-[#00a86b]"
                    />
                    <div>
                      <span className="text-white text-sm">{option.label}</span>
                      <p className="text-gray-400 text-xs">{option.description}</p>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Selected: {selectedLists.length} list{selectedLists.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Test Emails */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Test Email Addresses
              </label>
              <textarea
                value={testEmails}
                onChange={(e) => setTestEmails(e.target.value)}
                placeholder="admin@ciphersecurity.io, test@example.com"
                rows={2}
                className="w-full px-3 py-2 bg-[#0a0a0a] border border-white/[0.08] rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#00a86b] focus:border-white/[0.15] resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">
                Comma-separated email addresses for testing
              </p>
            </div>

            {/* Action Buttons */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
              <button
                onClick={handlePreview}
                disabled={!ctiData || isGeneratingPreview}
                className="flex items-center justify-center py-2 px-4 bg-[#0a0a0a] hover:bg-white/10 text-white rounded-lg transition-colors disabled:opacity-50 border border-white/[0.08]"
              >
                {isGeneratingPreview ? (
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Eye className="w-4 h-4 mr-2" />
                )}
                Preview
              </button>
              
              <button
                onClick={handleSendTest}
                disabled={!ctiData || !testEmails.trim() || isLoading}
                className="flex items-center justify-center py-2 px-4 bg-[#0a0a0a] hover:bg-white/10 text-white rounded-lg transition-colors disabled:opacity-50 border border-white/[0.08]"
              >
                <Mail className="w-4 h-4 mr-2" />
                Send Test
              </button>
              
              <button
                onClick={handleSendCampaign}
                disabled={!ctiData || selectedLists.length === 0 || isLoading}
                className="flex items-center justify-center py-2 px-4 bg-[#00a86b]/20 hover:bg-[#00a86b]/30 text-[#00a86b] rounded-lg transition-colors disabled:opacity-50 border border-[#00a86b]/40"
              >
                {isLoading ? (
                  <Loader className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Send Campaign
              </button>
            </div>

            {/* Status Messages */}
            <AnimatePresence mode="wait">
              {status === 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center p-3 bg-green-500/20 border border-green-500/40 rounded-lg text-green-200 text-sm"
                >
                  <CheckCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>{message}</span>
                </motion.div>
              )}
              
              {status === 'error' && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex items-center p-3 bg-red-500/20 border border-red-500/40 rounded-lg text-red-200 text-sm"
                >
                  <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span>{message}</span>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* CTI Content Preview */}
          <div className="bg-[#0a0a0a] backdrop-blur-sm rounded-xl p-6 border border-white/[0.08] flex flex-col">
            <h2 className="text-lg font-semibold text-white mb-4 flex items-center">
              <FileText className="w-4 h-4 mr-2" />
              Content Preview
            </h2>

            {ctiData ? (
              <div className="space-y-4 flex-1 overflow-y-auto">
                {/* Date */}
                {ctiData.date && (
                  <div className="bg-[#0a0a0a] rounded-lg p-3 border border-white/[0.08]">
                    <h3 className="text-white font-medium">Briefing Date</h3>
                    <p className="text-white">{ctiData.date}</p>
                  </div>
                )}

                {/* Headlines */}
                {ctiData.headlines && ctiData.headlines.length > 0 && (
                  <div className="bg-[#0a0a0a] rounded-lg p-3 border border-white/[0.08]">
                    <h3 className="text-white font-medium mb-2">Headlines ({ctiData.headlines.length})</h3>
                    {ctiData.headlines.map((headline, index) => (
                      <div key={index} className="border-l-2 border-red-500 pl-3 mb-3 last:mb-0">
                        <p className="text-white font-medium text-sm">{headline.title}</p>
                        <p className="text-gray-400 text-xs">{headline.date}</p>
                      </div>
                    ))}
                  </div>
                )}

                {/* Other News */}
                {ctiData.other_news && Object.keys(ctiData.other_news).length > 0 && (
                  <div className="bg-[#0a0a0a] rounded-lg p-3 border border-white/[0.08]">
                    <h3 className="text-white font-medium mb-2">Other News</h3>
                    {Object.entries(ctiData.other_news).map(([category, items], index) => (
                      <div key={index} className="mb-4 last:mb-0">
                        <p className="text-gray-300 text-sm font-medium mb-2">{category}</p>
                        <p className="text-gray-400 text-xs mb-2">{items.length} items</p>
                        <div className="space-y-1">
                          {items.map((item, itemIndex) => (
                            <div key={itemIndex} className="flex items-start">
                              <span className="text-[#00a86b] text-xs mr-2 mt-1">▶</span>
                              <p className="text-gray-300 text-xs">{item}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Technical Intelligence */}
                {ctiData.technical_intelligence && Object.keys(ctiData.technical_intelligence).length > 0 && (
                  <div className="bg-[#0a0a0a] rounded-lg p-3 border border-white/[0.08]">
                    <h3 className="text-white font-medium mb-2">Technical Intelligence</h3>
                    {Object.entries(ctiData.technical_intelligence).map(([section, items], index) => (
                      <div key={index} className="mb-4 last:mb-0">
                        <p className="text-gray-300 text-sm font-medium mb-2">{section}</p>
                        <p className="text-gray-400 text-xs mb-2">{items.length} items</p>
                        <div className="space-y-1">
                          {items.map((item, itemIndex) => (
                            <div key={itemIndex} className="flex items-start">
                              <span className="text-[#fbbf24] text-xs mr-2 mt-1">▶</span>
                              <p className="text-gray-300 text-xs">{item}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-400">Upload a CTI document to see content preview</p>
              </div>
            )}
          </div>
        </div>

        {/* Email Preview Modal */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-[90vw] max-h-[90vh] w-full bg-[#0a0a0a] border border-white/[0.08] p-0 overflow-hidden">
            <DialogHeader className="px-6 py-4 border-b border-white/[0.08]">
              <div className="flex items-center justify-between">
                <div>
                  <DialogTitle className="text-white text-lg font-semibold">CTI Briefing Email Preview</DialogTitle>
                  <DialogDescription className="text-gray-400 text-sm mt-1">
                    {customSubject || `🛡️ CIPHER Security CTI Briefing - ${ctiData?.date || 'Today'}`}
                  </DialogDescription>
                </div>
                <div className="flex items-center gap-2">
                  {/* Theme Toggle */}
                  <div className="flex bg-[#0a0a0a] rounded-lg border border-white/[0.08] p-1">
                    <button
                      onClick={() => setPreviewTheme('light')}
                      className={`p-2 rounded transition-colors ${
                        previewTheme === 'light'
                          ? 'bg-[#00a86b]/20 text-[#00a86b]'
                          : 'text-gray-400 hover:text-white'
                      }`}
                      title="Light Theme"
                    >
                      <Sun className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPreviewTheme('dark')}
                      className={`p-2 rounded transition-colors ${
                        previewTheme === 'dark'
                          ? 'bg-[#00a86b]/20 text-[#00a86b]'
                          : 'text-gray-400 hover:text-white'
                      }`}
                      title="Dark Theme"
                    >
                      <Moon className="w-4 h-4" />
                    </button>
                  </div>
                  
                  {/* Device Toggle */}
                  <div className="flex bg-[#0a0a0a] rounded-lg border border-white/[0.08] p-1">
                    <button
                      onClick={() => setPreviewDevice('desktop')}
                      className={`p-2 rounded transition-colors ${
                        previewDevice === 'desktop'
                          ? 'bg-[#00a86b]/20 text-[#00a86b]'
                          : 'text-gray-400 hover:text-white'
                      }`}
                      title="Desktop View"
                    >
                      <Monitor className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setPreviewDevice('mobile')}
                      className={`p-2 rounded transition-colors ${
                        previewDevice === 'mobile'
                          ? 'bg-[#00a86b]/20 text-[#00a86b]'
                          : 'text-gray-400 hover:text-white'
                      }`}
                      title="Mobile View"
                    >
                      <Smartphone className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </DialogHeader>
            
            {/* Preview Container */}
            <div className={`relative p-4 overflow-auto transition-colors duration-300 ${
              previewTheme === 'dark' ? 'bg-[#0a0a0a]' : 'bg-gray-100'
            }`} style={{ height: 'calc(90vh - 120px)' }}>
              <div className="flex justify-center">
                <div className={`transition-all duration-300 rounded-lg overflow-hidden border ${
                  previewDevice === 'mobile' ? 'w-[375px]' : 'w-full max-w-[800px]'
                } ${
                  previewTheme === 'dark' 
                    ? 'bg-[#1a1a2e] shadow-2xl shadow-black/50 border-white/20' 
                    : 'bg-white shadow-xl border-white/30'
                }`}>
                  {/* Email Client Header Mock */}
                  <div className={`px-4 py-3 transition-colors duration-300 ${
                    previewTheme === 'dark'
                      ? 'bg-[#0a0a0a]'
                      : 'bg-gray-50'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs ${
                        previewTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>From:</span>
                      <span className={`text-xs ${
                        previewTheme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                      }`}>CIPHER Security &lt;info@ciphersecurity.io&gt;</span>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`text-xs ${
                        previewTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>To:</span>
                      <span className={`text-xs ${
                        previewTheme === 'dark' ? 'text-gray-200' : 'text-gray-700'
                      }`}>subscriber@example.com</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-xs ${
                        previewTheme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>Subject:</span>
                      <span className={`text-xs font-medium ${
                        previewTheme === 'dark' ? 'text-gray-100' : 'text-gray-700'
                      }`}>
                        {customSubject || `🛡️ CIPHER Security CTI Briefing - ${ctiData?.date || 'Today'}`}
                      </span>
                    </div>
                  </div>
                  
                  {/* Separator/Gap */}
                  <div className={`h-3 transition-colors duration-300 ${
                    previewTheme === 'dark'
                      ? 'bg-[#0a0a0a] border-t border-white/[0.08]'
                      : 'bg-gray-50 border-t border-gray-200'
                  }`} />
                  
                  {/* Email Content Iframe */}
                  <iframe
                    ref={iframeRef}
                    className="w-full border-0"
                    style={{ 
                      height: previewDevice === 'mobile' ? '600px' : '800px',
                      backgroundColor: previewTheme === 'dark' ? '#0a0a0a' : '#ffffff',
                      colorScheme: previewTheme === 'dark' ? 'dark' : 'light',
                      width: '100%',
                      display: 'block'
                    }}
                    title="CTI Email Preview"
                    sandbox="allow-same-origin allow-scripts"
                  />
                </div>
              </div>
            </div>
            
            {/* Footer Actions */}
            <div className="px-6 py-3 border-t border-white/[0.08] bg-[#0a0a0a] flex justify-between items-center">
              <p className="text-xs text-gray-400">
                {previewTheme === 'dark' ? 'Dark theme (if supported by email client)' : 'Light theme (default)'} • {previewDevice === 'mobile' ? 'Mobile' : 'Desktop'} view
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => handleSendTest()}
                  disabled={!testEmails.trim() || isLoading}
                  className="px-4 py-2 bg-[#0a0a0a] hover:bg-white/10 text-white rounded-lg text-sm transition-colors disabled:opacity-50 border border-white/[0.08]"
                >
                  <Mail className="w-4 h-4 inline mr-2" />
                  Send Test
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg text-sm transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* NBA Content Warning Modal */}
        {nbaWarning.show && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-red-900/90 to-red-800/90 rounded-xl p-6 max-w-2xl w-full border border-red-500/30"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <AlertCircle className="w-8 h-8 text-red-400 mr-3" />
                  <div>
                    <h3 className="text-xl font-bold text-white">Non-Cybersecurity Content Detected</h3>
                    <p className="text-red-200">NBA/Sports content found in CTI briefing</p>
                  </div>
                </div>
                <button
                  onClick={() => setNbaWarning({show: false, terms: [], contexts: []})}
                  className="text-red-300 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="bg-black/30 rounded-lg p-4 mb-4">
                <h4 className="text-red-300 font-semibold mb-2">Flagged Terms:</h4>
                <div className="flex flex-wrap gap-2 mb-3">
                  {nbaWarning.terms.map((term, index) => (
                    <span key={index} className="bg-red-500/30 text-red-100 px-2 py-1 rounded text-sm">
                      {term}
                    </span>
                  ))}
                </div>
                
                {nbaWarning.contexts.length > 0 && (
                  <>
                    <h4 className="text-red-300 font-semibold mb-2">Context:</h4>
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {nbaWarning.contexts.map((context, index) => (
                        <div key={index} className="text-red-100 text-sm bg-red-900/30 p-2 rounded">
                          "{context}"
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setNbaWarning({show: false, terms: [], contexts: []});
                    setSelectedFile(null);
                    setStatus('error');
                    setMessage('Upload cancelled due to NBA content detection');
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors"
                >
                  Cancel Upload
                </button>
                <button
                  onClick={() => {
                    setNbaWarning({show: false, terms: [], contexts: []});
                    if (selectedFile) {
                      handleFileUpload(selectedFile, true);
                    } else {
                      setStatus('error');
                      setMessage('No file selected for force upload');
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-white rounded-lg transition-colors"
                >
                  Override & Continue
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CTIBriefingManager;